import { useMemo } from 'react';
import { StanBanner } from '@/components';
import IcInfo from '@/assets/icons/ic-info-circle.svg';
import type {
    FunnelPageItem,
    FunnelPageProductInfo,
    FunnelPageRouting,
    FunnelRouteTarget,
    FunnelRoutingEvent,
} from '../../../types';
import { DRAFT_FUNNEL_PAGE_ID, MAX_BRANCH_DEPTH } from '../../../constants/funnels.constants';
import { getFunnelPageDepth, getFunnelPageRelationships, isAtMaxBranchDepthFrom } from '../../../logic';

import { BrokenRouteWarning } from './BrokenRouteWarning';
import { RoutingRow } from './RoutingRow';

interface FunnelRoutingSectionProps {
    page: FunnelPageItem;
    stepNumber: number;
    sectionNumber: number;
    allPages: FunnelPageItem[];
    productInfoByPageId: Record<number, FunnelPageProductInfo | undefined>;
    routing: FunnelPageRouting | null;
    onChange: (next: FunnelPageRouting | null) => void;
    onDetachFromParent?: () => void;
    isDetaching?: boolean;
    disabled?: boolean;
}

export const FunnelRoutingSection = ({
    page,
    stepNumber,
    sectionNumber,
    allPages,
    productInfoByPageId,
    routing,
    onChange,
    onDetachFromParent,
    isDetaching,
    disabled,
}: FunnelRoutingSectionProps) => {
    // Pages already claimed as a routing target by some OTHER page. We keep nesting 1:1
    // so a creator can't route two different sources into the same child — they should
    // duplicate that page and branch the copy instead.
    const claimedByOthers = useMemo(() => {
        const claimed = new Map<number, number>();
        allPages.forEach(source => {
            if (source.page_id === page.page_id) return;
            if (!source.routing) return;
            (['on_purchase', 'on_skip'] as FunnelRoutingEvent[]).forEach(event => {
                const target = source.routing?.[event];
                if (target && !claimed.has(target.page_id)) {
                    claimed.set(target.page_id, source.page_id);
                }
            });
        });
        return claimed;
    }, [allPages, page.page_id]);

    const eligibleTargets = useMemo(
        () =>
            allPages.filter(
                p => p.page_id !== DRAFT_FUNNEL_PAGE_ID && p.page_id !== page.page_id && p.order > page.order
            ),
        [allPages, page.page_id, page.order]
    );

    const relationship = useMemo(
        () => getFunnelPageRelationships(allPages).get(page.page_id),
        [allPages, page.page_id]
    );
    const isBranchChild = relationship?.kind === 'upsell' || relationship?.kind === 'downsell';
    const branchContext =
        relationship?.kind === 'upsell'
            ? 'after a purchase'
            : relationship?.kind === 'downsell'
              ? 'when a visitor skips'
              : '';

    if (stepNumber === 1) return null;

    const sourceDepth = getFunnelPageDepth(allPages, page.page_id);
    const atMaxDepth = isAtMaxBranchDepthFrom(allPages, page.page_id);
    const hasNoTargets = eligibleTargets.length === 0;
    const noTargetsReason = 'This is the last page in the funnel, add another page to enable routing.';
    const maxDepthReason = `Branch chains are capped at ${MAX_BRANCH_DEPTH} levels deep (this page is at level ${sourceDepth + 1}). Detach it or shorten the chain to add more branches.`;

    const handleChange = (event: FunnelRoutingEvent, target: FunnelRouteTarget | null) => {
        const base: FunnelPageRouting = routing ?? { on_purchase: null, on_skip: null };
        const next: FunnelPageRouting = { ...base, [event]: target };
        // When the same target is selected for both events, clear the other event.
        if (target && event === 'on_purchase' && next.on_skip?.page_id === target.page_id) next.on_skip = null;
        if (target && event === 'on_skip' && next.on_purchase?.page_id === target.page_id) next.on_purchase = null;
        onChange(next.on_purchase || next.on_skip ? next : null);
    };

    // Skip is meaningless on the lead-capture step — nothing to opt out of yet.
    const isSkipDisabled = stepNumber === 1 || disabled;

    const purchaseId = routing?.on_purchase?.page_id ?? null;
    const skipId = routing?.on_skip?.page_id ?? null;

    const parentStep = relationship?.fromStep;
    const parentPage = parentStep ? allPages.find(p => p.order === parentStep) : null;
    const parentTitle = parentPage ? (productInfoByPageId[parentPage.page_id]?.title ?? parentPage.title) : null;

    const branchBannerTitle = (() => {
        if (parentTitle && parentStep) return `Shown ${branchContext} on “${parentTitle}” (Page ${parentStep})`;
        if (parentStep) return `Shown ${branchContext} on Page ${parentStep}`;
        return `Shown ${branchContext}`;
    })();

    const skipDisabledReason = (() => {
        if (hasNoTargets) return noTargetsReason;
        if (stepNumber === 1) return 'Visitors can’t skip the entry page.';
        return undefined;
    })();

    return (
        <div className='flex flex-col gap-4 md:gap-6'>
            <div className='flex flex-col gap-2'>
                <div className='flex items-center gap-3'>
                    <div className='bg-neutral-subtle flex size-8 items-center justify-center rounded-full'>
                        <span className='text-label-sm-bold text-dark'>{sectionNumber}</span>
                    </div>
                    <span className='text-heading-h4 text-dark'>Where should they go next?</span>
                </div>
                <p className='text-body-sm text-gray'>
                    Send customers down different paths depending on whether they purchase or pass on this offer.
                </p>
            </div>
            {isBranchChild && onDetachFromParent && (
                <StanBanner
                    variant='info'
                    icon={IcInfo}
                    title={branchBannerTitle}
                    subtitle='This page only appears along that path. Detach it to move it back into the main list as a standalone step.'
                    buttons={[
                        {
                            name: 'Detach',
                            variant: 'outline',
                            size: 'sm',
                            onClick: onDetachFromParent,
                            disabled: disabled || isDetaching,
                        },
                    ]}
                    ctaLoading={isDetaching}
                />
            )}
            <div className='flex flex-col gap-4 md:gap-6'>
                <div className='flex flex-col gap-3'>
                    <RoutingRow
                        event='on_purchase'
                        label='Upsell (if they buy this)'
                        helper='Where the customer goes after completing the purchase on this page.'
                        route={routing?.on_purchase ?? null}
                        eligibleTargets={eligibleTargets}
                        claimedByOthers={claimedByOthers}
                        claimedByOtherEvent={skipId}
                        productInfoByPageId={productInfoByPageId}
                        onChange={target => handleChange('on_purchase', target)}
                        disabled={disabled || hasNoTargets || (atMaxDepth && !routing?.on_purchase)}
                        disabledReason={
                            hasNoTargets
                                ? noTargetsReason
                                : atMaxDepth && !routing?.on_purchase
                                  ? maxDepthReason
                                  : undefined
                        }
                    />
                    <BrokenRouteWarning target={routing?.on_purchase ?? null} allPages={allPages} eventLabel='buy' />
                </div>

                <div className='flex flex-col gap-3'>
                    <RoutingRow
                        event='on_skip'
                        label='Downsell (if they skip this)'
                        helper='Where the customer goes when they decline or skip this offer.'
                        route={routing?.on_skip ?? null}
                        eligibleTargets={eligibleTargets}
                        claimedByOthers={claimedByOthers}
                        claimedByOtherEvent={purchaseId}
                        productInfoByPageId={productInfoByPageId}
                        onChange={target => handleChange('on_skip', target)}
                        disabled={isSkipDisabled || hasNoTargets || (atMaxDepth && !routing?.on_skip)}
                        disabledReason={
                            skipDisabledReason ?? (atMaxDepth && !routing?.on_skip ? maxDepthReason : undefined)
                        }
                    />

                    <BrokenRouteWarning target={routing?.on_skip ?? null} allPages={allPages} eventLabel='skip' />
                </div>
            </div>
        </div>
    );
};
